import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hook/useAuth";
import useToastNotification from "@/hook/useToastNotification";
import { sendCustomGaEvent } from "@/scripts/ga";
import {
  applyForCourse,
  getAppliedCourse,
} from "@/store/feature/courses/courseApi";
import {
  getAppliedApplications,
  getUploadedDocument,
  getUserDetail,
} from "@/store/feature/user/userApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Course, CourseItem } from "@/store/types/courseTypes";
import { FileCheck, Loader, Plus, Trash2 } from "lucide-react";
import { useRouter } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DocumentUploadOverlay from "./DocumentUploadOverlay";
import ConfirmExtractedDetails from "./DocumentUpload/ConfirmExtractedDetails";
import { useParallelDocumentUpload } from "./useParallelDocumentUpload";
import { BlueEditPencil } from "./Icons";
import Link from "next/link";
import { GraySelect } from "./ai-course-finder/GraySelect";
import { getCampusDetails } from "@/app/actions/CampusDetails";
import { CampusDetails } from "@/store/types/campusDetails";
import { academicRecords, qualificationOptions } from "./Helper";
import { XCircle, AlertTriangle, Info, X, Check } from "lucide-react";
import {
  setAiCourseFinderAcademicDetails,
  setAiCourseFinderEnglishProficiencyDetails,
} from "@/store/feature/ai-course-finder/aiCourseFinderSlice";
import { setApplicationRequirementsOpen } from "@/store/feature/common/commonSlice";
import axiosInstance from "@/utils/axiosInstance";
import { trackActivity } from "@/store/feature/activity/activityApi";

interface DocumentType {
  id: string;
  name: string;
  status: boolean;
  disabled?: boolean;
  optional?: boolean;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

interface DeleteDocumentModalProps {
  isOpen: boolean;
  documentName: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const getCountryName = (countryString?: string | null) => {
  const trimmedCountry = countryString?.trim();
  if (!trimmedCountry) return "";

  const countryParts = trimmedCountry.split(/\s+-\s+/).filter(Boolean);
  return countryParts[countryParts.length - 1]?.trim() || trimmedCountry;
};

const getCourseCountryName = (course?: Course | CourseItem) =>
  getCountryName((course as CourseItem)?.address_info?.country || course?.country);

const isNoEnglishTest = (test?: string | null) => {
  if (!test) return false;
  const normalized = test
    .toLowerCase()
    .trim()
    .replace(/['\u2019]/g, "")
    .replace(/[^a-z0-9]/g, "");
  return (
    normalized.includes("dont") ||
    normalized.includes("notest") ||
    normalized.includes("none") ||
    normalized.includes("waiver") ||
    normalized.includes("waive") ||
    normalized.includes("haveany") ||
    normalized === "no"
  );
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="ff_poppins fixed bottom-0 left-0 w-full h-full z-[999] flex items-end justify-center">
      <span
        className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer"
        onClick={onCancel}
      />
      <div className="bg-[#F8F9FF] text-[#1C2233] w-full max-w-md rounded-t-[32px] shadow-[0px_-4px_12px_0px_#ffffff40] overflow-hidden relative z-20 p-5 sm:p-8">
        <div className="mb-5 text-center">
          <h2 className="text-base sm:text-lg md:text-xl lg:text-[24px] font-semibold">
            Almost There!🚀
          </h2>
        </div>
        <div className="text-center">
          <p className="text-sm sm:text-base md:text-lg lg:text-xl ff_poppins mb-4">
            You&apos;re just a few uploads away from completing your
            application.
          </p>
          <div className="text-xs sm:text-sm md:text-md ff_poppins mb-8 text-[#808A9D]">
            <p className="mb-3">
              If you leave now, your progress will be saved — but your documents
              aren&apos;t uploaded yet.
            </p>
            <p>Do you want to finish now or come back later?</p>
          </div>
        </div>
        <div className="pt-0 grid grid-cols-2 gap-3">
          <p
            onClick={onCancel}
            className="text-[#1C2233] cursor-pointer border border-[#CBD5E1] rounded-full font-medium ff_poppins text-sm sm:text-md md:text-base text-center py-3"
          >
            Leave it
          </p>
          <button
            onClick={onConfirm}
            className="font-semibold cursor-pointer ff_poppins text-white text-sm md:text-lg p-3 rounded-full gradient_button_shadow bg-button-gradient text-center flex items-center justify-center gap-2"
          >
            Finish Now
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteDocumentModal: React.FC<DeleteDocumentModalProps> = ({
  isOpen,
  documentName,
  isDeleting,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 left-0 w-full h-full z-[1000] flex items-end justify-center">
      <span
        className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer"
        onClick={onCancel}
      />
      <div className="bg-[#F8F9FF] text-[#1C2233] rounded-t-[32px] w-full max-w-md p-6 ff_poppins relative z-20 shadow-[0px_-4px_12px_0px_#ffffff40]">
        <h3 className="text-lg font-semibold mb-3">Delete Document</h3>
        <p className="text-sm text-[#808A9D] mb-6">
          Are you sure you want to delete{" "}
          <span className="font-medium">{documentName}</span>?
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-3 rounded-full border border-[#CBD5E1] text-[#1C2233] text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-3 rounded-full bg-red-600 text-white text-sm disabled:opacity-70"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ApplicationRequirementsPopUp({
  setApplicationRequirementPopUp,
  selectedCourse,
  setIsOpen,
  isAiCourseFinder = false,
}: {
  setApplicationRequirementPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCourse: Course | CourseItem;
  isAiCourseFinder: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const {
    appliedCourseData,
    academicDetails: courseAcademicDetails,
    englishProficiencyDetails: courseEnglishDetails,
    loading,
  } = useAppSelector((state) => state.course);
  const {
    academicDetails: aiAcademicDetails,
    englishProficiencyDetails: aiEnglishDetails,
  } = useAppSelector((state) => state.aiCourseFinder);

  const [selectedCampus, setSelectedCampus] = useState<string>("");
  const [selectedCampusDetails, setSelectedCampusDetails] =
    useState<CampusDetails | null>(null);
  const [campusLoading, setCampusLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const { documents: userDocument, userDetails } = useAppSelector(
    (state) => state.userProfile,
  );
  const router = useRouter();
  const dispatch = useAppDispatch();

  const academicDetails = isAiCourseFinder
    ? aiAcademicDetails
    : courseAcademicDetails;
  const englishProficiencyDetails = isAiCourseFinder
    ? aiEnglishDetails
    : courseEnglishDetails;
  const selectedCourseCountryName = getCourseCountryName(selectedCourse);

  const [selectedIntake, setSelectedIntake] = useState<{
    month: string;
    status: string;
    deadline: string;
  } | null>(null);

  const countryData = appliedCourseData.find(
    (item) =>
      getCountryName(item?.countryName).toLowerCase() ===
      selectedCourseCountryName.toLowerCase(),
  );

  const { showToast } = useToastNotification();

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [uploadOverlayOpen, setUploadOverlayOpen] = useState(false);
  const [currentDocumentId, setCurrentDocumentId] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const isSubmitted = React.useRef(false);

  useEffect(() => {
    return () => {
      if (!isSubmitted.current) {
        dispatch(
          trackActivity({
            source: "application_abandon",
            input: {
              courseId: String(selectedCourse?.course_id || (selectedCourse as any)?.courseId),
              courseName: (selectedCourse as Course)?.course_name || (selectedCourse as any)?.courseName,
            },
          })
        ).catch(() => { });
      }
    };
  }, [dispatch, selectedCourse]);

  const [activeTab, setActiveTab] = useState<string>("");
  const [forceShowVerification, setForceShowVerification] =
    useState<boolean>(false);
  const [deletingDocType, setDeletingDocType] = useState<string>("");
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    docType: string;
    docName: string;
  }>({ isOpen: false, docType: "", docName: "" });

  const {
    jobs,
    startExtraction,
    setJobState,
    removeJob,
    submitVerification,
    updateExtractedData,
  } = useParallelDocumentUpload(selectedCourse?.course_id, user?.uid);

  const normalizeDocumentId = (value: string) =>
    (value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  const tabPriority: Record<string, number> = {
    phd_certificate: 10,
    phd_transcript: 11,
    master_marksheet: 20,
    master_degree: 20,
    master_certificate: 20,
    master_transcript: 21,
    pg_diploma_marksheet: 30,
    pg_diploma_degree: 30,
    pg_diploma_certificate: 30,
    pg_diploma_transcript: 31,
    bachelor_marksheet: 40,
    bachelor_certificate: 40,
    bachelor_transcript: 41,
    diploma_marksheet: 50,
    diploma_degree: 50,
    diploma_certificate: 50,
    diploma_transcript: 51,
    "12th_marksheet": 60,
    "10th_marksheet": 70,
    english_proficiency: 800,
    moi_certificate: 810,
    passport: 900,
  };

  const getDocumentPriority = (docId: string) => {
    const normalized = normalizeDocumentId(docId);
    if (tabPriority[normalized] !== undefined) return tabPriority[normalized];

    const isTranscript = normalized.includes("transcript");

    if (normalized.includes("phd")) return isTranscript ? 11 : 10;
    if (normalized.includes("master")) return isTranscript ? 21 : 20;
    if (normalized.includes("pg_diploma") || normalized.includes("pgdiploma"))
      return isTranscript ? 31 : 30;
    if (normalized.includes("bachelor")) return isTranscript ? 41 : 40;
    if (normalized.includes("diploma")) return isTranscript ? 51 : 50;
    if (normalized.includes("12th")) return 60;
    if (normalized.includes("10th")) return 70;
    if (normalized.includes("english")) return 800;
    if (normalized.includes("moi")) return 810;
    if (normalized.includes("passport")) return 900;

    return 1000;
  };

  const isDocumentCompleted = (doc: DocumentType) => {
    const jobStatus = jobs[doc.id]?.status;
    return doc.status || jobStatus === "completed";
  };

  const reviewJobs = Object.values(jobs)
    .filter((j) => j.status === "verification_pending" || j.status === "saving")
    .sort((a, b) => {
      const priorityA = getDocumentPriority(a.docId);
      const priorityB = getDocumentPriority(b.docId);
      if (priorityA === priorityB)
        return a.documentName.localeCompare(b.documentName);
      return priorityA - priorityB;
    });

  const activeReviewJob =
    activeTab &&
      jobs[activeTab] &&
      (jobs[activeTab].status === "verification_pending" ||
        jobs[activeTab].status === "saving")
      ? jobs[activeTab]
      : null;

  useEffect(() => {
    if (
      reviewJobs.length > 0 &&
      (!activeTab ||
        !jobs[activeTab] ||
        (jobs[activeTab].status !== "verification_pending" &&
          jobs[activeTab].status !== "saving"))
    ) {
      setActiveTab(reviewJobs[0].docId);
    }
  }, [jobs, activeTab, reviewJobs]);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [noEnglishTest, setNoEnglishTest] = useState(false);

  // ✅ FIX: Default to "12th" but sync from academicDetails.pastQualification
  const [selectedHighestQualification, setSelectedHighestQualification] =
    useState("12th");

  const [eligibilityResult, setEligibilityResult] = useState<{
    isEligible: boolean;
    reasons: string[];
  } | null>(null);
  const [showEligibilityWarning, setShowEligibilityWarning] = useState(false);
  const [hasShownWarning, setHasShownWarning] = useState(false);

  useEffect(() => {
    void sendCustomGaEvent("application_requirements_popup_view", {
      course_id: selectedCourse?.course_id || "",
      is_ai_course_finder: isAiCourseFinder,
    });
  }, [isAiCourseFinder, selectedCourse?.course_id]);

  const [showMismatchAlert, setShowMismatchAlert] = useState(false);
  const [mismatchDetails, setMismatchDetails] = useState<string[]>([]);
  const [mismatchAccepted, setMismatchAccepted] = useState(false);
  const optionalDocumentTypes = useMemo(() => new Set(["moi_certificate"]), []);

  const lastCheckedProfileRef = React.useRef<string | null>(null);

  const normalizeQualificationTitle = useCallback((value: string) => {
    const normalized = (value || "")
      .toLowerCase()
      .trim()
      .replace(/['\u2019]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

    if (!normalized) return "";
    if (
      normalized.includes("pg diploma") ||
      normalized.includes("post graduate diploma")
    )
      return "pg_diploma";
    if (normalized.includes("undergraduate") || normalized.includes("bachelor"))
      return "bachelor";
    if (normalized.includes("postgraduate") || normalized.includes("master"))
      return "master";
    if (normalized.includes("phd") || normalized.includes("doctorate"))
      return "phd";
    if (normalized.includes("diploma")) return "diploma";
    if (
      normalized.includes("certification") ||
      normalized.includes("certificate")
    )
      return "certification";
    if (normalized.startsWith("12") || normalized.includes("senior secondary"))
      return "12th";
    if (normalized.startsWith("10") || normalized.includes("secondary"))
      return "10th";
    return normalized.replace(/\s+/g, "_");
  }, []);

  const getProfileHighestQualification = useCallback(() => {
    const educationDetails = userDetails?.education_details;
    if (!Array.isArray(educationDetails) || educationDetails.length === 0)
      return "";

    const getPriority = (title: string) => {
      const normalized = normalizeQualificationTitle(title);
      const matched = qualificationOptions.find(
        (option) => normalizeQualificationTitle(option.title) === normalized,
      );
      return matched?.priority ?? Infinity;
    };

    const highest = educationDetails.reduce((prev: any, current: any) =>
      getPriority(current?.qualification_level) <
        getPriority(prev?.qualification_level)
        ? current
        : prev,
    );

    return highest?.qualification_level || "";
  }, [normalizeQualificationTitle, userDetails?.education_details]);

  const isDocumentUploaded = useCallback(
    (documentType: string) => {
      if (userDocument && Array.isArray(userDocument)) {
        return userDocument.some((doc) => doc.doc_type === documentType);
      }
      return false;
    },
    [userDocument],
  );

  const getUploadedDocumentByType = useCallback(
    (documentType: string) => {
      if (!Array.isArray(userDocument)) return null;
      return userDocument.find((doc) => doc.doc_type === documentType) ?? null;
    },
    [userDocument],
  );

  const isVerifiedDocument = (status?: string) => {
    const normalizedStatus = (status || "").toLowerCase();
    return normalizedStatus === "verified" || normalizedStatus === "completed";
  };

  const canDeleteUploadedDocument = (documentType: string) => {
    const existingDoc = getUploadedDocumentByType(documentType);
    return !!existingDoc?.doc_id && !isVerifiedDocument(existingDoc.doc_status);
  };

  useEffect(() => {
    const testType = englishProficiencyDetails?.test;
    if (isNoEnglishTest(testType)) {
      setNoEnglishTest(true);
    } else if (testType) {
      setNoEnglishTest(false);
    } else if (isDocumentUploaded("moi_certificate")) {
      setNoEnglishTest(true);
    } else if (isDocumentUploaded("english_proficiency")) {
      setNoEnglishTest(false);
    }
  }, [userDocument, englishProficiencyDetails?.test, isDocumentUploaded]);

  useEffect(() => {
    if (academicDetails?.pastQualification) {
      setSelectedHighestQualification(academicDetails.pastQualification);
    } else {
      const profileHighestQualification = getProfileHighestQualification();
      setSelectedHighestQualification(profileHighestQualification || "12th");
    }
  }, [academicDetails?.pastQualification, getProfileHighestQualification]);

  useEffect(() => {
    if (user?.uid) {
      dispatch(getUploadedDocument(user.uid));
    }
  }, [user?.uid, dispatch]);

  useEffect(() => {
    if (!academicDetails && !englishProficiencyDetails) return;

    const profileFingerprint = JSON.stringify({
      edu: userDetails?.education_details?.length ?? 0,
      eng: userDetails?.english_proficiency_test?.length ?? 0,
      eduFirst: userDetails?.education_details?.[0]?.qualification_level ?? "",
      engFirst: userDetails?.english_proficiency_test?.[0]?.testName ?? "",
    });

    if (lastCheckedProfileRef.current === profileFingerprint) return;
    lastCheckedProfileRef.current = profileFingerprint;

    const hasEducation = (userDetails?.education_details?.length ?? 0) > 0;
    const hasEnglishTest =
      (userDetails?.english_proficiency_test?.length ?? 0) > 0;

    const normalize = (val: string) => {
      if (!val) return "";
      const v = val
        .toLowerCase()
        .trim()
        .replace(/[''\u2019]s$/, "");
      if (v === "bachelor") return "bachelor";
      if (v === "master") return "master";
      return v;
    };

    const getPriority = (title: string) => {
      const normalized = normalize(title);
      const opt = qualificationOptions.find(
        (o) => normalize(o.title) === normalized,
      );
      return opt?.priority ?? Infinity;
    };

    const diffs: string[] = [];

    if (academicDetails?.pastQualification && hasEducation) {
      const profileHighest = userDetails!.education_details!.reduce(
        (highest: any, current: any) =>
          getPriority(current.qualification_level) <
            getPriority(highest.qualification_level)
            ? current
            : highest,
        userDetails!.education_details![0],
      );

      if (profileHighest) {
        const searchQual = normalize(academicDetails.pastQualification);
        const profileQual = normalize(profileHighest.qualification_level);

        if (searchQual && profileQual && searchQual !== profileQual) {
          diffs.push(
            `Qualification: "${academicDetails.pastQualification}" in search vs "${profileHighest.qualification_level}" in profile`,
          );
        }

        if (
          academicDetails.pastGrades &&
          profileHighest.cgpa_or_percentage_value
        ) {
          const searchGrades = String(academicDetails.pastGrades).trim();
          const profileGrades = String(
            profileHighest.cgpa_or_percentage_value,
          ).trim();
          if (searchGrades !== profileGrades) {
            diffs.push(
              `Grades: "${searchGrades}" in search vs "${profileGrades}" in profile`,
            );
          }
        }
      }
    }

    if (englishProficiencyDetails?.test && hasEnglishTest) {
      const profileTest = userDetails!.english_proficiency_test![0];
      const searchTest = normalize(englishProficiencyDetails.test);
      const profileTestName = normalize(profileTest?.testName || "");

      if (searchTest && profileTestName && searchTest !== profileTestName) {
        diffs.push(
          `English Test: "${englishProficiencyDetails.test}" in search vs "${profileTest?.testName}" in profile`,
        );
      }

      if (englishProficiencyDetails.overallScore && profileTest?.overall) {
        const searchOverall = String(
          englishProficiencyDetails.overallScore,
        ).trim();
        const profileOverall = String(profileTest.overall).trim();
        if (searchOverall !== profileOverall) {
          diffs.push(
            `Overall Score: "${searchOverall}" in search vs "${profileOverall}" in profile`,
          );
        }
      }
    }

    if (diffs.length > 0) {
      setMismatchDetails(diffs);
      setMismatchAccepted(false);
    } else {
      setMismatchDetails([]);
      setShowMismatchAlert(false);
    }
  }, [userDetails, academicDetails, englishProficiencyDetails]);

  const handleUseProfileDetails = async () => {
    setShowMismatchAlert(false);
    setMismatchDetails([]);
    setApplicationRequirementPopUp(false);
    dispatch(setApplicationRequirementsOpen(false));
  };

  const campusOptions = useMemo(() => {
    if (!selectedCourse?.campus_name) return [];
    return selectedCourse.campus_name
      .split(",")
      .map((campus) => campus.trim())
      .filter((campus) => campus.length > 0)
      .map((item, idx) => ({ id: idx, title: item }));
  }, [selectedCourse?.campus_name]);

  useEffect(() => {
    if (userDocument === undefined || userDocument === null) return;

    const selectedQualification = normalizeQualificationTitle(
      selectedHighestQualification,
    );
    const matched = academicRecords.find(
      (item) =>
        normalizeQualificationTitle(item.title) === selectedQualification,
    );
    const requiredOptions = matched?.options || [];

    const baseDocuments = [
      {
        id: "passport",
        name: "Passport",
        status: false,
        disabled: false,
        optional: false,
      },
    ];

    if (noEnglishTest) {
      baseDocuments.push({
        id: "moi_certificate",
        name: "Medium of Instruction",
        status: false,
        disabled: false,
        optional: true,
      });
    } else {
      baseDocuments.push({
        id: "english_proficiency",
        name: "English Proficiency",
        status: false,
        disabled: false,
        optional: false,
      });
    }

    baseDocuments.push(
      {
        id: "10th_marksheet",
        name: "10th Marksheet",
        status: false,
        disabled: false,
        optional: false,
      },
      {
        id: "12th_marksheet",
        name: "12th Marksheet",
        status: false,
        disabled: false,
        optional: false,
      },
    );

    const requiredDocuments = [...baseDocuments];

    requiredOptions.forEach((opt) => {
      if (!requiredDocuments.some((d) => d.id === opt.value)) {
        requiredDocuments.push({
          id: opt.value,
          name: opt.title,
          status: false,
          disabled: false,
          optional: optionalDocumentTypes.has(opt.value),
        });
      }
    });

    const orderedRequiredDocuments = [...requiredDocuments].sort((a, b) => {
      const priorityA = getDocumentPriority(a.id);
      const priorityB = getDocumentPriority(b.id);
      if (priorityA === priorityB) return a.name.localeCompare(b.name);
      return priorityA - priorityB;
    });

    if (Array.isArray(userDocument)) {
      setDocuments(
        orderedRequiredDocuments.map((doc) => {
          const existingDoc = userDocument.find(
            (userDoc) => userDoc.doc_type === doc.id,
          );
          return existingDoc ? { ...doc, status: true, disabled: true } : doc;
        }),
      );
    } else {
      setDocuments(orderedRequiredDocuments);
    }
  }, [
    selectedHighestQualification,
    noEnglishTest,
    userDocument,
    optionalDocumentTypes,
    normalizeQualificationTitle,
  ]);

  useEffect(() => {
    if (!isAiCourseFinder) return;
    if (!selectedCourse || !academicDetails || !englishProficiencyDetails)
      return;

    const criteriaIds = [
      "pastQualification",
      "twelfthPercentage",
      "boardName",
      "english",
      "education_gap",
    ];
    const failedReasons: string[] = [];

    for (const id of criteriaIds) {
      const result = checkEligibilityForCriterion(id, selectedCourse);
      if (!result.isEligible && result.reason)
        failedReasons.push(result.reason);
    }

    const isEligible = failedReasons.length === 0;
    setEligibilityResult({ isEligible, reasons: failedReasons });

    if (!isEligible && !hasShownWarning) {
      setShowEligibilityWarning(true);
      setHasShownWarning(true);
    }
  }, [
    isAiCourseFinder,
    selectedCourse,
    academicDetails,
    englishProficiencyDetails,
  ]);

  const intakes =
    selectedCourse?.intake_details?.month &&
    selectedCourse.intake_details.month.map((month: string, index: number) => ({
      month,
      status: selectedCourse.intake_details?.status[index],
      deadline: selectedCourse.intake_details?.deadline[index],
    }));

  const formatMonthYear = (monthYear: string) => {
    const parts = monthYear.split("/");
    let month: number;
    let year: number;

    if (parts.length === 3) {
      const [, m, y] = parts;
      month = Number(m);
      year = Number(y);
    } else if (parts.length === 2) {
      const [d, m] = parts;
      month = Number(d);
      year = Number(m);
    } else {
      return monthYear;
    }

    return new Date(year, month - 1).toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
  };

  const handleIntakeSelect = (intake: {
    month: string;
    status: string | undefined;
    deadline: string | undefined;
  }) => {
    if (selectedIntake?.month === intake.month) {
      setSelectedIntake(null);
    } else {
      setSelectedIntake({
        month: intake.month,
        status: intake.status || "",
        deadline: intake.deadline || "",
      });
      setErrorMsg("");
    }
  };

  const handleCampusSelection = async (campusName: string) => {
    setCampusLoading(true);
    const formattedCampusName = campusName.replace("-", " ").trim();
    setSelectedCampus(formattedCampusName);
    setErrorMsg("");

    try {
      const response = await getCampusDetails(formattedCampusName);
      setSelectedCampusDetails(
        Object.keys(response).length > 0 ? response : null,
      );
    } catch (error) {
      console.error("Error fetching campus details:", error);
      setSelectedCampusDetails(null);
    } finally {
      setCampusLoading(false);
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      if (isDocumentUploaded("english_proficiency")) {
        showToast(
          "Please delete the English Proficiency document before selecting this option.",
          "error",
        );
        return;
      }
      setNoEnglishTest(true);
    } else {
      if (isDocumentUploaded("moi_certificate")) {
        showToast(
          "Please delete the MOI certificate before adding an English test.",
          "error",
        );
        return;
      }
      setNoEnglishTest(false);
    }
  };

  const openUploadOverlay = (documentId: string) => {
    const doc = documents.find((d) => d.id === documentId);
    if (doc && doc.disabled) return;
    setCurrentDocumentId(documentId);
    setUploadOverlayOpen(true);
  };

  const closeUploadOverlay = () => {
    setUploadOverlayOpen(false);
    setCurrentDocumentId("");
  };

  const markDocumentAsUploaded = (documentId: string) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === documentId ? { ...doc, status: true, disabled: true } : doc,
      ),
    );
  };

  const handleDocumentFileAccepted = async (file: File) => {
    const documentId = currentDocumentId;
    const existingJob = jobs[documentId];

    if (
      existingJob &&
      (existingJob.status === "extracting" ||
        existingJob.status === "uploading" ||
        existingJob.status === "verification_pending")
    ) {
      return;
    }

    closeUploadOverlay();
    const uploadStatus = await startExtraction(
      documentId,
      getCurrentDocumentName(),
      file,
    );
    if (uploadStatus === "completed") markDocumentAsUploaded(documentId);
  };

  const getCurrentDocumentName = () => {
    const doc = documents.find((d) => d.id === currentDocumentId);
    return doc ? doc.name : "";
  };

  const handleDeleteDocument = async (documentType: string) => {
    const existingDoc = getUploadedDocumentByType(documentType);
    if (!existingDoc?.doc_id || isVerifiedDocument(existingDoc.doc_status))
      return;

    setDeletingDocType(documentType);
    try {
      await axiosInstance.delete(`/documents/delete/${existingDoc.doc_id}`);
      showToast("Document deleted successfully", "success");
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === documentType
            ? { ...doc, status: false, disabled: false }
            : doc,
        ),
      );
      if (jobs[documentType]) removeJob(documentType);
      if (activeTab === documentType) {
        setForceShowVerification(false);
        setActiveTab("");
      }
      if (user?.uid) {
        void dispatch(getUploadedDocument(user.uid));
        void dispatch(getUserDetail(user.uid));
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showToast("Failed to delete document. Please try again.", "error");
    } finally {
      setDeletingDocType("");
      setDeleteModalState({ isOpen: false, docType: "", docName: "" });
    }
  };

  const hasProgress = useCallback(() => {
    return (
      selectedIntake ||
      documents.some((doc) => isDocumentCompleted(doc) || !!jobs[doc.id]) ||
      agreeToTerms ||
      (campusOptions.length > 1 && selectedCampus)
    );
  }, [
    selectedIntake,
    documents,
    agreeToTerms,
    campusOptions,
    selectedCampus,
    jobs,
  ]);

  const closePopup = () => {
    setApplicationRequirementPopUp(false);
    dispatch(setApplicationRequirementsOpen(false));
  };

  const handleClose = () => {
    void sendCustomGaEvent("application_requirements_popup_close", {
      course_id: selectedCourse?.course_id || "",
    });
    if (hasProgress()) {
      setShowConfirmation(true);
    } else {
      closePopup();
    }
  };

  const handleConfirmClose = () => setShowConfirmation(false);

  const handleCancelClose = () => {
    setShowConfirmation(false);
    closePopup();
  };

  const checkEligibilityForCriterion = (
    id: string,
    course: Course | CourseItem,
  ): { isEligible: boolean; reason?: string } => {
    const normalizeQualificationLevel = (value?: string) => {
      const normalized = (value || "")
        .toLowerCase()
        .trim()
        .replace(/['\u2019]s$/, "");
      if (normalized === "bachelors" || normalized === "undergraduate")
        return "bachelor";
      if (normalized === "masters" || normalized === "postgraduate")
        return "master";
      return normalized;
    };

    const qualificationRank: Record<string, number> = {
      "10th": 1,
      secondary: 1,
      ssc: 1,
      "12th": 2,
      "senior secondary": 2,
      hsc: 2,
      diploma: 3,
      bachelor: 4,
      ug: 4,
      master: 5,
      pg: 5,
      phd: 6,
      doctorate: 6,
    };

    const getQualificationRank = (value?: string) =>
      qualificationRank[normalizeQualificationLevel(value)] ?? 0;

    const isNullishOrMissing = (obj: any, keyPath: string[]): boolean => {
      let val = obj;
      for (const key of keyPath) {
        if (val == null || !(key in val)) return true;
        val = val[key];
      }
      return val == null;
    };

    switch (id) {
      case "pastQualification": {
        const courseLevel = normalizeQualificationLevel(
          course.course_details?.course_level,
        );
        const pastQual = normalizeQualificationLevel(
          academicDetails.pastQualification,
        );

        if (pastQual === "12th") {
          const validLevels = ["bachelor", "diploma", "certification"];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your qualification (12th) is not sufficient for ${courseLevel} level courses. Valid course levels: Bachelor's, Diploma, or Certification.`,
            };
          }
        } else if (pastQual === "bachelor") {
          const validLevels = [
            "bachelor",
            "master",
            "diploma",
            "certification",
          ];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your qualification is not sufficient for ${courseLevel} level courses. Valid course levels: Master's, Diploma, or Certification.`,
            };
          }
        } else if (pastQual === "master" || pastQual === "pg") {
          const validLevels = ["master", "phd", "doctorate", "certification"];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your past qualification (Master's) cannot be used for ${courseLevel} level courses. To apply for a ${courseLevel} course, your past qualification should be 12th, Diploma, or Bachelor's.`,
            };
          }
        } else if (pastQual === "diploma") {
          const validLevels = ["bachelor", "diploma", "certification"];
          if (!validLevels.includes(courseLevel || "")) {
            return {
              isEligible: false,
              reason: `Your qualification (Diploma) is not sufficient for ${courseLevel} level courses. Valid course levels: Bachelor's, Diploma, or Certification.`,
            };
          }
        }

        if (isNullishOrMissing(course, ["pre_requisite_details", "level"])) {
          return { isEligible: true };
        }

        const requiredQualification = course.pre_requisite_details?.level || "";
        const requiredRank = getQualificationRank(requiredQualification);
        const studentRank = getQualificationRank(
          academicDetails.pastQualification,
        );

        if (requiredRank > 0 && studentRank > 0 && studentRank < requiredRank) {
          return {
            isEligible: false,
            reason: `Required qualification level: ${requiredQualification}.`,
          };
        }

        return { isEligible: true };
      }

      case "twelfthPercentage": {
        if (isNullishOrMissing(course, ["twelfth_percentage"]))
          return { isEligible: true };

        const studentPercentage = parseFloat(academicDetails.pastGrades);
        let requiredPercentage = course.twelfth_percentage as any;
        if (typeof requiredPercentage === "object" && requiredPercentage !== null) {
          const boardName = englishProficiencyDetails?.selectBoard || "";
          const boardValue = requiredPercentage[boardName];
          if (boardValue !== undefined && boardValue !== null) {
            requiredPercentage = Number(boardValue);
          } else {
            const values = Object.values(requiredPercentage).map(Number).filter(v => !isNaN(v));
            requiredPercentage = values.length > 0 ? Math.min(...values) : 0;
          }
        } else {
          requiredPercentage = Number(requiredPercentage) || 0;
        }

        if (requiredPercentage > 0 && requiredPercentage <= 1) {
          requiredPercentage = requiredPercentage * 100;
        }

        if (studentPercentage < requiredPercentage) {
          return {
            isEligible: false,
            reason: `Your 12th percentage (${studentPercentage}%) is below the required ${requiredPercentage}%.`,
          };
        }

        return { isEligible: true };
      }

      case "boardName": {
        if (
          isNullishOrMissing(course, ["state_board_not_accepted"]) ||
          !course.state_board_not_accepted?.length
        ) {
          return { isEligible: true };
        }

        const selectedBoard = englishProficiencyDetails.selectBoard || "";
        const isNotAccepted =
          course.state_board_not_accepted.includes(selectedBoard);

        if (isNotAccepted) {
          return {
            isEligible: false,
            reason: `Your board (${selectedBoard}) is not accepted for this course.`,
          };
        }

        return { isEligible: true };
      }

      case "english": {
        if (
          isNullishOrMissing(course, ["twelfth_english_percentage"]) &&
          isNullishOrMissing(course, ["englishProficiencyRequirements"])
        ) {
          return { isEligible: true };
        }

        if (isNoEnglishTest(englishProficiencyDetails?.test)) {
          if (!course.english_waiver_accepted) {
            return {
              isEligible: false,
              reason: "English waiver is not accepted for this course.",
            };
          }

          if (course.english_waiver_state_boards_accepted) {
            try {
              const acceptedBoards = JSON.parse(course.english_waiver_state_boards_accepted);
              if (Array.isArray(acceptedBoards) && englishProficiencyDetails.selectBoard) {
                const boardName = englishProficiencyDetails.selectBoard.toLowerCase();
                const isBoardAccepted = acceptedBoards.some(board => 
                  board.toLowerCase().includes(boardName) ||
                  boardName.includes(board.toLowerCase())
                );
                
                if (!isBoardAccepted) {
                  return {
                    isEligible: false,
                    reason: `Your board (${englishProficiencyDetails.selectBoard}) is not accepted for an English waiver for this course.`,
                  };
                }
              }
            } catch (e) {
              console.error("Failed to parse english_waiver_state_boards_accepted", e);
            }
          }

          const studentMarks = parseFloat(
            englishProficiencyDetails.englishMarks || "0",
          );
          let requiredMarks = course.twelfth_english_percentage as any;
          if (typeof requiredMarks === "object" && requiredMarks !== null) {
            const boardName = englishProficiencyDetails?.selectBoard || "";
            const boardValue = requiredMarks[boardName];
            if (boardValue !== undefined && boardValue !== null) {
              requiredMarks = Number(boardValue);
            } else {
              const values = Object.values(requiredMarks).map(Number).filter(v => !isNaN(v));
              requiredMarks = values.length > 0 ? Math.min(...values) : 0;
            }
          } else {
            requiredMarks = Number(requiredMarks) || 0;
          }

          if (requiredMarks > 0 && requiredMarks <= 1) {
            requiredMarks = requiredMarks * 100;
          }

          if (
            !isNullishOrMissing(course, ["twelfth_english_percentage"]) &&
            studentMarks < requiredMarks
          ) {
            return {
              isEligible: false,
              reason: `Your 12th English marks (${studentMarks}%) are below the required ${requiredMarks}%.`,
            };
          }

          return { isEligible: true };
        }

        const testType = englishProficiencyDetails.test;
        if (!testType) return { isEligible: true };

        const requirement = course.englishProficiencyRequirements?.[testType];

        if (!requirement) return { isEligible: true };

        const studentScores = {
          Overall: parseFloat(englishProficiencyDetails.overallScore || "0"),
          Reading: parseFloat(englishProficiencyDetails.readingScore || "0"),
          Writing: parseFloat(englishProficiencyDetails.writingScore || "0"),
          Speaking: parseFloat(englishProficiencyDetails.speakingScore || "0"),
          Listening: parseFloat(
            englishProficiencyDetails.listeningScore || "0",
          ),
        };

        const failedSections = [];
        if (studentScores.Overall < (requirement.Overall ?? 0))
          failedSections.push(
            `Overall: ${studentScores.Overall} (required: ${requirement.Overall})`,
          );
        if (studentScores.Reading > 0 && studentScores.Reading < (requirement.Reading ?? 0))
          failedSections.push(
            `Reading: ${studentScores.Reading} (required: ${requirement.Reading})`,
          );
        if (studentScores.Writing > 0 && studentScores.Writing < (requirement.Writing ?? 0))
          failedSections.push(
            `Writing: ${studentScores.Writing} (required: ${requirement.Writing})`,
          );
        if (studentScores.Speaking > 0 && studentScores.Speaking < (requirement.Speaking ?? 0))
          failedSections.push(
            `Speaking: ${studentScores.Speaking} (required: ${requirement.Speaking})`,
          );
        if (studentScores.Listening > 0 && studentScores.Listening < (requirement.Listening ?? 0))
          failedSections.push(
            `Listening: ${studentScores.Listening} (required: ${requirement.Listening})`,
          );

        if (failedSections.length > 0) {
          return {
            isEligible: false,
            reason: `${englishProficiencyDetails.test} scores below requirement: ${failedSections.join(", ")}.`,
          };
        }

        return { isEligible: true };
      }

      case "education_gap": {
        if (!academicDetails.passingYear) return { isEligible: true };

        const currentYear = new Date().getFullYear();
        const passingYear = parseInt(academicDetails.passingYear);

        if (isNaN(passingYear) || passingYear > currentYear) {
          return {
            isEligible: false,
            reason: `Invalid passing year: ${academicDetails.passingYear}.`,
          };
        }

        const calculatedGap = currentYear - passingYear;
        const allowedGap = course.gap_allowed ?? calculatedGap;

        if (calculatedGap > allowedGap) {
          return {
            isEligible: false,
            reason: `Your education gap (${calculatedGap} years) exceeds the maximum allowed gap of ${allowedGap} years.`,
          };
        }

        return { isEligible: true };
      }

      default:
        return { isEligible: true };
    }
  };

  const handleSubmitApplication = async () => {
    void sendCustomGaEvent("application_requirements_submit_attempt", {
      course_id: selectedCourse?.course_id || "",
      has_selected_intake: !!selectedIntake,
      documents_count: documents.length,
    });
    if (!selectedIntake) {
      setErrorMsg("Please select an intake.");
      return;
    }

    if (campusOptions.length > 0 && !selectedCampus) {
      setErrorMsg("Please select a campus.");
      return;
    }

    if (documents.some((doc) => !doc.optional && !isDocumentCompleted(doc))) {
      setErrorMsg("Please upload all required documents.");
      return;
    }

    if (!agreeToTerms) {
      setErrorMsg("Please agree to the terms and conditions.");
      return;
    }

    if (mismatchDetails.length > 0 && !mismatchAccepted) {
      setShowMismatchAlert(true);
      return;
    }

    const criteriaIds = [
      "pastQualification",
      "twelfthPercentage",
      "boardName",
      "english",
      "education_gap",
    ];
    const failedReasons: string[] = [];

    for (const id of criteriaIds) {
      const result = checkEligibilityForCriterion(id, selectedCourse);
      if (!result.isEligible && result.reason)
        failedReasons.push(result.reason);
    }

    if (failedReasons.length > 0) {
      setEligibilityResult({ isEligible: false, reasons: failedReasons });
      setShowEligibilityWarning(true);
      return;
    }

    const countryName =
      getCountryName(selectedCampusDetails?.country) ||
      selectedCourseCountryName;

    if (!countryName) {
      setErrorMsg("Unable to identify the course country. Please try again.");
      return;
    }

    setEligibilityResult(null);
    setSubmitting(true);
    setErrorMsg("");

    const addressInfo = selectedCampusDetails
      ? { city: selectedCampusDetails.city, state: selectedCampusDetails.state }
      : {
        city: (selectedCourse as CourseItem)?.address_info?.city,
        state: (selectedCourse as CourseItem)?.address_info?.state,
      };

    const payload = {
      studentId: user?.uid ?? "",
      courseId: selectedCourse?.course_id,
      countryName,
      courseDetails: selectedCourse?.course_details,
      intakeDetails: {
        month: selectedIntake.month,
        status: selectedIntake.status,
        deadline: selectedIntake.deadline,
      },
      addressInfo,
      collegeName:
        (selectedCourse as CourseItem)?.universitiesName ?? selectedCourse?.college_name,
      ...(selectedCampus && { campusName: selectedCampus }),
    };

    try {
      const res = await dispatch(applyForCourse(payload));

      if (applyForCourse.fulfilled.match(res)) {
        if (res.payload.status) {
          void sendCustomGaEvent("application_requirements_submit_success", {
            course_id: selectedCourse?.course_id || "",
          });
          showToast("Application submitted successfully!", "success");
          isSubmitted.current = true;
          closePopup();
          if (user?.uid) {
            await dispatch(getAppliedApplications(user.uid));
            await dispatch(getAppliedCourse(user.uid));
          }
          router.push("/dashboard");
        } else {
          showToast(res.payload.message ?? "Application failed!", "error");
        }
      }

      if (applyForCourse.rejected.match(res)) {
        void sendCustomGaEvent("application_requirements_submit_failed", {
          course_id: selectedCourse?.course_id || "",
        });
        showToast("Failed to submit application.", "error");
        console.error("Error payload:", res.payload);
      }
    } catch (error) {
      void sendCustomGaEvent("application_requirements_submit_failed", {
        course_id: selectedCourse?.course_id || "",
      });
      showToast("Failed to submit application. Please try again.", "error");
      console.error("Caught error:", error);
    } finally {
      setSubmitting(false);
      if (user?.uid) {
        await dispatch(getAppliedCourse(user.uid));
      }
    }
  };

  const handleEdit = () => {
    closePopup();
    setIsOpen(true);
  };

  const handleEditAiCourse = () => {
    router.push("/ai-course-finder/eligibility?query=pick-country");
    closePopup();
  };

  const isAnyProcessing = Object.values(jobs).some(
    (j) => j.status === "extracting" || j.status === "uploading",
  );

  const allUploadedOrPending = documents.every(
    (d) =>
      d.optional ||
      d.status ||
      (jobs[d.id] &&
        (jobs[d.id].status === "verification_pending" ||
          jobs[d.id].status === "saving")) ||
      (jobs[d.id] && jobs[d.id].status === "completed"),
  );

  const shouldShowVerifyModal =
    reviewJobs.length > 0 &&
    (forceShowVerification || (!isAnyProcessing && allUploadedOrPending));

  const requirementSteps = [
    !!selectedIntake,
    campusOptions.length === 0 || !!selectedCampus,
    documents.every((doc) => doc.optional || isDocumentCompleted(doc)),
    agreeToTerms,
  ];
  const completedSteps = requirementSteps.filter(Boolean).length;
  const progressPercent = Math.round((completedSteps / requirementSteps.length) * 100);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] flex items-end justify-center p-0">
        <span
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
        />
        <div className="relative z-[120] w-full max-w-md md:max-w-[640px] max-h-[90vh] overflow-y-auto scrollBarHidden rounded-t-3xl shadow-2xl bg-[#F8F9FF] text-[#1C2233] ff_poppins p-6 sm:p-8 md:p-10">
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 p-2 bg-[#EFEFFF] hover:bg-[#E2E2FF] rounded-full transition-colors cursor-pointer"
          >
            <X className="w-5 h-5 text-[#525B73]" />
          </button>
          <div className="w-12 h-1.5 bg-[#D9D9D9] rounded-full mx-auto mb-4" />
          <div className="mb-6 pr-12">
            <p className="text-[#3B82F6] text-xs font-bold tracking-wider uppercase mb-1">
              Apply now
            </p>
            <h2 className="text-xl sm:text-2xl font-bold text-[#1C2233]">
              Application Requirements
            </h2>
            <p className="text-xs sm:text-sm text-[#808A9D] mt-1">
              Complete each step to submit your application.
            </p>
          </div>
          <div className="mb-8">
            <div className="flex gap-2 h-2 w-full">
              {requirementSteps.map((done, idx) => (
                <div
                  key={idx}
                  className={`flex-1 rounded-full transition-colors duration-300 ${
                    done ? "bg-[#3B82F6]" : "bg-[#E2E8F0]"
                  }`}
                />
              ))}
            </div>
            <div className="flex justify-end mt-1.5">
              <span className="text-xs text-[#808A9D] font-medium">
                {progressPercent}% done
              </span>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <p className="text-[#808A9D] text-xs font-semibold tracking-wider uppercase mb-3">
                Step 1 · Select intake
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {intakes &&
                  intakes
                    .filter((intakeObj) => intakeObj.status?.toLowerCase() === "open")
                    .map((intakeObj) => {
                      const isSelected = selectedIntake?.month === intakeObj.month;
                      return (
                        <button
                          key={intakeObj.month}
                          type="button"
                          onClick={() => handleIntakeSelect(intakeObj)}
                          className={`flex items-center gap-3 p-4 rounded-2xl border transition-all text-left shadow-sm ff_poppins text-sm font-semibold text-[#1C2233] cursor-pointer ${
                            isSelected
                              ? "border-[#3B82F6] ring-1 ring-[#3B82F6] bg-[#F0F6FF]"
                              : "border-[#E2E8F0] bg-white hover:border-[#CBD5E1]"
                          }`}
                        >
                          <span
                            className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                              isSelected
                                ? "bg-[#3B82F6] border-[#3B82F6] text-white"
                                : "border-[#CBD5E1] bg-white"
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                          </span>
                          {formatMonthYear(intakeObj.month)}
                        </button>
                      );
                    })}
              </div>
            </div>

            {campusOptions.length > 0 && (
              <div>
                <p className="text-[#808A9D] text-xs font-semibold ff_poppins tracking-wider uppercase mb-3">
                  Step 2 · Select campus
                  {campusLoading && (
                    <Loader size={14} className="inline ml-2 animate-spin" />
                  )}
                </p>
                <div>
                  <GraySelect
                    value={selectedCampus}
                    onChange={handleCampusSelection}
                    options={campusOptions}
                    placeholder={
                      campusLoading ? "Loading..." : "Select a campus"
                    }
                    isEditAble={campusLoading}
                    className="w-full md:w-auto min-w-[250px] rounded-lg border border-gray-300 bg-white"
                  />
                </div>
              </div>
            )}

            <div className="mt-3">
              <p className="text-[#808A9D] text-xs font-semibold ff_poppins tracking-wider uppercase mb-3">
                Step 3 · Upload documents
              </p>

              {(!englishProficiencyDetails?.test ||
                isNoEnglishTest(englishProficiencyDetails?.test)) && (
                  <div className="flex items-center gap-2 mb-4">
                    <Checkbox
                      id="no-english-test"
                      checked={noEnglishTest}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(checked === true)
                      }
                    />
                    <label
                      htmlFor="no-english-test"
                      className="text-sm font-medium ff_poppins cursor-pointer"
                    >
                      I don&apos;t have any English test
                    </label>
                  </div>
                )}

              <p className="text-[#808A9D] font-medium ff_poppins text-sm mb-4">
                Accepted file types include PDF and DOCX, with a maximum file
                size of 5 MB.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {documents.map((doc) => {
                  const job = jobs[doc.id];
                  const isCompleted = isDocumentCompleted(doc);
                  const isProcessing =
                    job &&
                    (job.status === "extracting" || job.status === "uploading");
                  const isPending =
                    job && job.status === "verification_pending";
                  const isSaving = job && job.status === "saving";
                  const hasError = job && job.status === "error";
                  const uploadedDoc = getUploadedDocumentByType(doc.id);
                  const isDeleting = deletingDocType === doc.id;
                  const canDelete = canDeleteUploadedDocument(doc.id);

                  return (
                    <div key={doc.id}>
                      <button
                        className={`w-full flex-col flex items-center justify-center gap-1 py-3 sm:py-4 px-3 rounded-2xl border text-[#1C2233] ${isCompleted
                          ? "border-[#A7F3D0] bg-[#ECFDF5] hover:bg-[#D1FAE5]"
                          : "border-[#E2E8F0] bg-white hover:border-[#3B82F6] hover:bg-[#F0F6FF]"
                          } ${isProcessing || isSaving
                            ? "cursor-not-allowed opacity-70"
                            : "cursor-pointer"
                          }`}
                        onClick={() => {
                          if (hasError) removeJob(doc.id);
                          if (isPending || isSaving) {
                            setActiveTab(doc.id);
                            setForceShowVerification(true);
                            return;
                          }
                          if (
                            !isProcessing &&
                            !isSaving &&
                            !isCompleted &&
                            !doc.disabled
                          ) {
                            openUploadOverlay(doc.id);
                          }
                        }}
                        disabled={isProcessing || isSaving}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {isProcessing ? (
                            <>
                              <Loader
                                size={20}
                                className="animate-spin text-blue-600"
                              />
                              <span className="ff_poppins text-sm sm:text-md">
                                {job.status === "extracting"
                                  ? "Extracting..."
                                  : "Uploading..."}
                              </span>
                            </>
                          ) : isSaving ? (
                            <>
                              <Loader
                                size={20}
                                className="animate-spin text-blue-600"
                              />
                              <span className="ff_poppins text-sm sm:text-md">
                                Saving...{doc.optional ? " (Optional)" : ""}
                              </span>
                            </>
                          ) : doc.status || job?.status === "completed" ? (
                            <>
                              <FileCheck size={20} />
                              <span className="truncate max-w-[150px]">
                                {doc.name}
                                {doc.optional ? " (Optional)" : ""}
                              </span>
                            </>
                          ) : isPending ? (
                            <>
                              <AlertTriangle
                                size={20}
                                className="text-yellow-600"
                              />
                              <span className="ff_poppins text-sm sm:text-md text-yellow-700">
                                Click Here to verify Details
                                {doc.optional ? " (Optional)" : ""}
                              </span>
                            </>
                          ) : (
                            <>
                              <Plus size={20} />
                              <span className="ff_poppins text-sm sm:text-md">
                                {doc.name}
                                {doc.optional ? " (Optional)" : ""}
                              </span>
                            </>
                          )}
                        </div>
                        {canDelete && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isDeleting) return;
                              setDeleteModalState({
                                isOpen: true,
                                docType: doc.id,
                                docName:
                                  uploadedDoc?.original_filename || doc.name,
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (isDeleting) return;
                                setDeleteModalState({
                                  isOpen: true,
                                  docType: doc.id,
                                  docName:
                                    uploadedDoc?.original_filename || doc.name,
                                });
                              }
                            }}
                            className={`mt-1 text-red-500 hover:text-red-700 transition-colors p-1 ${isDeleting
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                              }`}
                            title="Delete Document"
                          >
                            {isDeleting ? (
                              <Loader size={14} className="animate-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </span>
                        )}
                        {hasError && (
                          <span className="text-xs text-red-500 font-medium px-2">
                            {job.errorMsg || "Failed"}
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-start gap-2 mt-5">
              <Checkbox
                id="terms"
                checked={agreeToTerms}
                onCheckedChange={(checked) => {
                  setAgreeToTerms(checked === true);
                  setErrorMsg("");
                }}
                className="mt-1"
              />
              <label
                htmlFor="terms"
                className="text-xs sm:text-sm font-normal ff_poppins"
              >
                I hereby accept the{" "}
                <Link
                  target="_blank"
                  href={"/policies/terms-conditions"}
                  className="text-[#3777FF] underline"
                >
                  terms and conditions
                </Link>{" "}
                of the platform <br className="hidden sm:block" /> and agree to
                abide by its{" "}
                <Link
                  target="_blank"
                  href={"/policies/privacy-policy"}
                  className="text-[#3777FF] underline"
                >
                  policies and guidelines.
                </Link>
              </label>
            </div>

            {errorMsg && (
              <div className="text-red-500 text-sm mb-4 ff_poppins">
                {errorMsg}
              </div>
            )}

            {eligibilityResult && !eligibilityResult.isEligible && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="text-red-500 w-5 h-5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800 ff_poppins mb-1">
                      You do not meet the prerequisites for this course
                    </p>
                    {eligibilityResult.reasons.length > 0 ? (
                      <ul className="text-xs text-red-600 ff_poppins list-disc pl-4 space-y-1">
                        {eligibilityResult.reasons.map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-red-600 ff_poppins">
                        Based on your current details, you are not eligible for
                        this course.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="ff_poppins bg-[#EFF6FF] border border-[#DBEAFE] text-[#1E40AF] rounded-2xl text-sm sm:text-base font-medium flex justify-center items-center p-2">
              Want to change the eligibility details?{" "}
              <span
                onClick={() =>
                  isAiCourseFinder ? handleEditAiCourse() : handleEdit()
                }
                className="flex items-center gap-2 text-xs sm:text-sm md:text-base text-nowrap text-[#3B82F6] ml-2 cursor-pointer"
              >
                <BlueEditPencil /> Edit details now
              </span>
            </div>

            <div className="ff_poppins bg-[#EFF6FF] border border-[#DBEAFE] text-[#1E40AF] rounded-2xl text-xs flex justify-center items-center p-2">
              {`Please note: You can apply to a maximum of "${countryData?.maxUnisPerCountry || 5
                } universities per country" and "${countryData?.courseLimitPerUni || 1
                } course per university". Choose wisely to make the most of your opportunities!`}
            </div>

            <div className="flex items-center justify-between gap-4 pt-6 border-t border-[#E2E8F0]">
              <button
                className="px-6 py-2.5 rounded-full border border-[#3B82F6] text-[#3B82F6] font-semibold text-sm hover:bg-[#F0F6FF] transition-colors"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                disabled={submitting || loading || campusLoading}
                onClick={handleSubmitApplication}
                className={`flex-1 max-w-xs py-3 rounded-full bg-[#3B82F6] text-white font-semibold text-sm hover:bg-[#2563EB] transition-colors shadow-md flex items-center justify-center gap-2 ${submitting || loading || campusLoading
                  ? "cursor-not-allowed opacity-80"
                  : "cursor-pointer"
                  }`}
              >
                {submitting ? (
                  <>
                    <Loader size={20} className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </div>
        </div>

        {submitting && (
          <div className="fixed inset-0 bg-white/60 backdrop-blur-[2px] flex flex-col items-center justify-center z-[2000] rounded-2xl">
            <Loader size={48} className="text-[#3777FF] animate-spin mb-4" />
            <p className="text-[#3777FF] font-bold text-xl ff_poppins animate-pulse">
              Submitting your application...
            </p>
          </div>
        )}

        {!documents.find((d) => d.id === currentDocumentId)?.disabled && (
          <DocumentUploadOverlay
            key={currentDocumentId || "document-upload-overlay"}
            isOpen={uploadOverlayOpen}
            handleClose={closeUploadOverlay}
            onClose={closeUploadOverlay}
            documentName={getCurrentDocumentName()}
            studentId={user?.uid}
            onFileAccepted={handleDocumentFileAccepted}
          />
        )}
      </div>

      {shouldShowVerifyModal && (
        <div className="fixed bottom-0 left-0 w-full h-full z-[1000] flex items-end justify-center">
          <span
            className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer"
            onClick={() => setForceShowVerification(false)}
          />
          <div className="bg-[#F8F9FF] text-[#1C2233] w-full max-w-3xl rounded-t-[32px] shadow-[0px_-4px_12px_0px_#ffffff40] overflow-hidden relative z-20 flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b border-[#E2E8F0]">
              <h2 className="text-xl font-semibold text-[#1C2233] ff_poppins">
                Verify Extracted Details
              </h2>
              <button
                onClick={() => setForceShowVerification(false)}
                className="text-[#525B73] hover:text-[#1C2233] font-bold px-2"
              >
                X
              </button>
            </div>

            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="flex gap-2 overflow-x-auto border-b border-[#E2E8F0] px-6 pt-4">
                {reviewJobs.map((job) => (
                  <button
                    key={job.docId}
                    onClick={() => setActiveTab(job.docId)}
                    className={`px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap outline-none transition-colors ${activeTab === job.docId
                      ? "border-[#3B82F6] text-[#1C2233] bg-white rounded-t-lg"
                      : "border-transparent text-[#808A9D] hover:text-[#1C2233] hover:border-[#CBD5E1]"
                      }`}
                  >
                    {job.documentName}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto w-full p-2">
                {activeReviewJob?.extractedData && (
                  <ConfirmExtractedDetails
                    inline
                    documentName={activeReviewJob.documentName}
                    extractedData={activeReviewJob.extractedData}
                    editErrors={activeReviewJob.editErrors || {}}
                    isLoading={activeReviewJob.status === "saving"}
                    onChange={(field, value) =>
                      updateExtractedData(activeTab, field, value)
                    }
                    onConfirm={async () => {
                      const completedDocId = activeTab;
                      if (!completedDocId) return;
                      const isCompleted =
                        await submitVerification(completedDocId);
                      if (isCompleted) {
                        markDocumentAsUploaded(completedDocId);
                        removeJob(completedDocId);
                      }
                    }}
                    onClose={() => removeJob(activeTab)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />

      <DeleteDocumentModal
        isOpen={deleteModalState.isOpen}
        documentName={deleteModalState.docName}
        isDeleting={
          !!deleteModalState.docType &&
          deletingDocType === deleteModalState.docType
        }
        onCancel={() =>
          setDeleteModalState({ isOpen: false, docType: "", docName: "" })
        }
        onConfirm={() => handleDeleteDocument(deleteModalState.docType)}
      />

      {showMismatchAlert && mismatchDetails.length > 0 && (
        <div className="fixed bottom-0 left-0 w-full h-full z-[1000] flex items-end justify-center">
          <span
            className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer"
            onClick={() => setShowMismatchAlert(false)}
          />
          <div className="bg-[#F8F9FF] text-[#1C2233] w-full max-w-md rounded-t-[32px] shadow-[0px_-4px_12px_0px_#ffffff40] overflow-hidden relative z-20 p-6 sm:p-8">
            <div className="flex flex-col items-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <Info className="text-blue-600 w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-[#1C2233] mb-2 ff_poppins">
                Your Details Don&apos;t Match
              </h2>
              <p className="text-[#808A9D] mb-4 ff_poppins text-sm">
                The course search details you used are different from
                what&apos;s saved in your profile. This can change your
                eligibility outcome.
              </p>
              <ul className="text-sm text-[#808A9D] ff_poppins list-disc list-inside mb-6 text-left w-full">
                {mismatchDetails.map((diff, idx) => (
                  <li key={idx} className="mb-1">
                    {diff}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={handleUseProfileDetails}
                  className="w-full py-3 sm:py-4 px-6 font-semibold ff_poppins text-white text-sm md:text-base rounded-full gradient_button_shadow bg-button-gradient text-center hover:scale-105 duration-300 ease-in-out"
                >
                  Refresh With Profile Details
                </button>
                <button
                  onClick={() => {
                    setMismatchAccepted(true);
                    setShowMismatchAlert(false);
                  }}
                  className="w-full py-3 sm:py-4 px-6 font-semibold ff_poppins text-sm md:text-base rounded-full border border-[#3B82F6] text-[#3B82F6] hover:bg-[#F0F6FF] transition-colors"
                >
                  Continue With Current Details
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowMismatchAlert(false)}
              className="absolute top-4 right-4 text-[#808A9D] hover:text-[#1C2233]"
            >
              <XCircle size={24} />
            </button>
          </div>
        </div>
      )}

      {showEligibilityWarning && (
        <div className="fixed bottom-0 left-0 w-full h-full z-[1000] flex items-end justify-center">
          <span
            className="inset-0 bg-black/60 backdrop-blur-[6px] absolute top-0 left-0 w-full h-full cursor-pointer"
            onClick={() => setShowEligibilityWarning(false)}
          />
          <div className="bg-[#F8F9FF] text-[#1C2233] w-full max-w-md rounded-t-[32px] shadow-[0px_-4px_12px_0px_#ffffff40] overflow-hidden relative z-20 p-6 sm:p-8">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-100 p-3 rounded-full mb-4">
                <AlertTriangle className="text-red-600 w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-[#1C2233] mb-2 ff_poppins">
                Prerequisites Not Met
              </h2>
              <div className="text-[#808A9D] mb-6 ff_poppins text-left">
                {eligibilityResult?.reasons &&
                  eligibilityResult.reasons.length > 0 ? (
                  <ul className="list-disc pl-4 space-y-1 text-sm">
                    {eligibilityResult.reasons.map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                ) : (
                  <p>
                    Based on your current details, you do not meet the
                    prerequisites for this course.
                  </p>
                )}
              </div>
              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={() => {
                    setShowEligibilityWarning(false);
                    isAiCourseFinder ? handleEditAiCourse() : handleEdit();
                  }}
                  className="w-full py-4 px-6 bg-button-gradient text-white rounded-full font-semibold ff_poppins shadow-lg transition-colors"
                >
                  Update My Details
                </button>
                <button
                  onClick={() => {
                    setShowEligibilityWarning(false);
                    closePopup();
                  }}
                  className="w-full py-4 px-6 border border-[#3B82F6] text-[#3B82F6] rounded-full font-semibold ff_poppins hover:bg-[#F0F6FF] transition-colors"
                >
                  Go Back
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowEligibilityWarning(false)}
              className="absolute top-4 right-4 text-[#808A9D] hover:text-[#1C2233]"
            >
              <XCircle size={24} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
